import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AuthLayout, { Field, SubmitButton } from "@/pages/AuthLayout";
import { Mail, Smartphone, CheckCircle2 } from "lucide-react";

export default function Verify() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [emailCode, setEmailCode] = useState("");
  const [mobileCode, setMobileCode] = useState("");
  const [mockCode, setMockCode] = useState(sessionStorage.getItem("mock_mobile_code") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user.role !== "patient") navigate(`/${user.role}`);
    if (user && user.email_verified) {
      // fine to stay for mobile, but push to consent
    }
  }, [user, navigate]);

  const verifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/verify-email", { code: emailCode });
      await refreshUser();
      toast.success("Email verified");
    } catch (e2) { setError(apiError(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyMobile = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/verify-mobile", { code: mobileCode });
      await refreshUser();
      toast.success("Mobile verified");
    } catch (e2) { setError(apiError(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-code");
      if (data.mock_mobile_code) {
        setMockCode(data.mock_mobile_code);
        sessionStorage.setItem("mock_mobile_code", data.mock_mobile_code);
      }
      toast.success("New codes sent");
    } catch { toast.error("Could not resend"); }
  };

  if (!user) return null;

  return (
    <AuthLayout title="Verify your account" subtitle="Confirm your email and mobile to continue to consent.">
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 p-5 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="h-4.5 w-4.5 h-[18px] w-[18px] text-brand" />
            <span className="font-head font-semibold text-ink">Email verification</span>
            {user.email_verified && <CheckCircle2 className="h-5 w-5 text-green-600 ml-auto" />}
          </div>
          {user.email_verified ? (
            <p className="text-sm text-green-700">Your email is verified.</p>
          ) : (
            <form onSubmit={verifyEmail}>
              <p className="text-sm text-ink-soft mb-3">We emailed a 6-digit code to <span className="font-medium">{user.email}</span>.</p>
              <Field label="Email code" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} data-testid="verify-email-code" placeholder="123456" />
              <SubmitButton loading={loading} type="submit" data-testid="verify-email-submit">Verify email</SubmitButton>
            </form>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 p-5 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Smartphone className="h-[18px] w-[18px] text-brand" />
            <span className="font-head font-semibold text-ink">Mobile verification</span>
            {user.mobile_verified && <CheckCircle2 className="h-5 w-5 text-green-600 ml-auto" />}
          </div>
          {user.mobile_verified ? (
            <p className="text-sm text-green-700">Your mobile is verified.</p>
          ) : (
            <form onSubmit={verifyMobile}>
              {mockCode && (
                <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                  MOCK SMS — your code is <span className="mono font-bold">{mockCode}</span>
                </div>
              )}
              {!mockCode && (
                <div className="mb-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-brand">
                  We texted a 6-digit code to your phone{user.mobile ? ` (${user.mobile})` : ""}.
                </div>
              )}
              <Field label="Mobile code" value={mobileCode} onChange={(e) => setMobileCode(e.target.value)} data-testid="verify-mobile-code" placeholder="123456" />
              <SubmitButton loading={loading} type="submit" data-testid="verify-mobile-submit">Verify mobile</SubmitButton>
            </form>
          )}
        </div>

        {error && <p className="text-sm text-red-600" data-testid="verify-error">{error}</p>}

        <div className="flex items-center justify-between">
          <button onClick={resend} className="text-sm text-brand font-medium hover:underline" data-testid="verify-resend">Resend codes</button>
          <button
            onClick={() => navigate("/patient/consent")}
            data-testid="verify-continue"
            className="rounded-lg bg-brand text-white font-semibold px-5 py-2.5 hover:bg-brand-hover transition-colors disabled:opacity-50"
            disabled={!user.email_verified}
          >
            Continue to consent
          </button>
        </div>
      </div>
    </AuthLayout>
  );
}
